import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { getDateFnsLocale } from "@/lib/date-fns-locale";
import { apiFetch } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import { fetchShoppingList } from "@/lib/shopping-list-api";
import type { ShoppingListItem, ShoppingListResponse, ShoppingListSection } from "@shared/api";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { bottomSheetDockClassName } from "@/lib/bottom-sheet-compact";
import { cn } from "@/lib/utils";
import { PageTitleBar } from "@/components/ui/PageTitleBar";
import { useIsMobile } from "@/hooks/use-mobile";
import { FabMenu } from "@/components/ui/fab-menu";
import Loading from "@/components/Loading";
import { AppEmptyState } from "@/components/ui/app-empty-state";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Plus,
  Trash2,
  ChevronDown,
  Car,
  ShoppingCart,
  Loader2,
  ChevronRight,
  ListPlus,
  GripVertical,
  List,
  Package,
  Store,
  ShoppingBag,
  Home,
} from "lucide-react";

const SECTION_ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  car: Car,
  "shopping-cart": ShoppingCart,
  list: List,
  package: Package,
  store: Store,
  "shopping-bag": ShoppingBag,
  home: Home,
};

function SectionIcon({ section }: { section: ShoppingListSection }) {
  const Icon =
    section.icon && SECTION_ICON_MAP[section.icon]
      ? SECTION_ICON_MAP[section.icon]
      : section.sort_order === 0
        ? Car
        : section.sort_order === 1
          ? ShoppingCart
          : List;
  return <Icon className="h-4 w-4 shrink-0" />;
}

function getSectionIconOptions(t: (key: string) => string) {
  return [
    { value: "", label: "—", Icon: List },
    { value: "car", label: t("shoppingList.iconCar"), Icon: Car },
    { value: "shopping-cart", label: t("shoppingList.iconCart"), Icon: ShoppingCart },
    { value: "list", label: t("shoppingList.iconList"), Icon: List },
    { value: "package", label: t("shoppingList.iconPackage"), Icon: Package },
    { value: "store", label: t("shoppingList.iconStore"), Icon: Store },
    { value: "shopping-bag", label: t("shoppingList.iconBag"), Icon: ShoppingBag },
    { value: "home", label: t("shoppingList.iconHome"), Icon: Home },
  ];
}

async function fetchList(): Promise<ShoppingListResponse> {
  return fetchShoppingList();
}

function formatItemDate(iso: string | null | undefined, lng: string): string {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return format(d, "dd.MM.yyyy HH:mm", { locale: getDateFnsLocale(lng) });
  } catch {
    return iso;
  }
}

function ShoppingListPage() {
  const { t, i18n } = useTranslation();
  const isMobile = useIsMobile();
  const qc = useQueryClient();
  const { toast } = useToast();
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [completingId, setCompletingId] = useState<string | null>(null);

  function handleDeleteItem(id: string) {
    setDeletingId(id);
    setTimeout(() => deleteMutation.mutate(id), 320);
  }
  const [deleteSectionState, setDeleteSectionState] = useState<{
    section: ShoppingListSection;
    itemCount: number;
  } | null>(null);
  const [deleteSectionMoveToId, setDeleteSectionMoveToId] = useState<string>("");
  const [sectionInputs, setSectionInputs] = useState<Record<string, { title: string; note: string }>>({});
  const [newSectionModalOpen, setNewSectionModalOpen] = useState(false);
  const [newSectionName, setNewSectionName] = useState("");
  const [newSectionIcon, setNewSectionIcon] = useState<string>("");

  const listQuery = useQuery({
    queryKey: queryKeys.shoppingList,
    queryFn: fetchList,
  });

  const createMutation = useMutation({
    mutationFn: async (payload: { section_id: string; title: string; note?: string }) => {
      const res = await apiFetch("/api/shopping-list", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as { message?: string }).message || "Create failed");
      }
      return res.json();
    },
    onSuccess: (_, v) => {
      qc.invalidateQueries({ queryKey: queryKeys.shoppingList });
      setSectionInputs((prev) => ({
        ...prev,
        [v.section_id]: { title: "", note: "" },
      }));
    },
  });

  const createSectionMutation = useMutation({
    mutationFn: async (payload: { name: string; icon?: string | null }) => {
      const res = await apiFetch("/api/shopping-list/sections", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as { message?: string }).message || "Create section failed");
      }
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.shoppingList });
      setNewSectionModalOpen(false);
      setNewSectionName("");
      setNewSectionIcon("");
      toast({ title: t("shoppingList.sectionCreated"), variant: "default" });
    },
    onError: (e) => {
      toast({ title: e.message, variant: "destructive" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({
      id,
      updates,
    }: {
      id: string;
      updates: {
        title?: string;
        note?: string | null;
        checked?: boolean;
        sort_order?: number;
        section_id?: string;
      };
    }) => {
      const res = await apiFetch(`/api/shopping-list/${id}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updates),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as { message?: string }).message || "Update failed");
      }
      return res.json();
    },
    onSuccess: async (_data, variables) => {
      await qc.invalidateQueries({ queryKey: queryKeys.shoppingList });
      setCompletingId((prev) => (prev === variables.id ? null : prev));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiFetch(`/api/shopping-list/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) throw new Error("Delete failed");
    },
    onSuccess: async (_data, id: string) => {
      await qc.invalidateQueries({ queryKey: queryKeys.shoppingList });
      setDeletingId((prev) => (prev === id ? null : prev));
    },
  });

  const deleteSectionMutation = useMutation({
    mutationFn: async ({
      sectionId,
      moveTo,
      deleteItems,
    }: {
      sectionId: string;
      moveTo?: string;
      deleteItems?: boolean;
    }) => {
      const params = new URLSearchParams();
      if (moveTo) params.set("moveTo", moveTo);
      if (deleteItems) params.set("deleteItems", "1");
      const res = await apiFetch(
        `/api/shopping-list/sections/${sectionId}?${params.toString()}`,
        { method: "DELETE", credentials: "include" }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as { message?: string }).message || "Delete section failed");
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.shoppingList });
      setDeleteSectionState(null);
      setDeleteSectionMoveToId("");
      toast({ title: t("shoppingList.sectionDeleted"), variant: "default" });
    },
    onError: (e) => {
      toast({ title: e.message, variant: "destructive" });
    },
  });

  const sections = listQuery.data?.sections ?? [];
  const items = listQuery.data?.items ?? [];

  function itemsForSection(sectionId: string): ShoppingListItem[] {
    return items
      .filter((i) => i.section_id === sectionId)
      .sort((a, b) => a.sort_order - b.sort_order);
  }

  function pendingCountForSection(sectionId: string): number {
    return itemsForSection(sectionId).filter((i) => !i.checked).length;
  }

  function getSectionInputs(sectionId: string) {
    return sectionInputs[sectionId] ?? { title: "", note: "" };
  }

  function handleReorder(currentOrder: ShoppingListItem[], newOrder: ShoppingListItem[]) {
    if (currentOrder.length !== newOrder.length) return;
    for (let i = 0; i < currentOrder.length; i++) {
      if (currentOrder[i].id !== newOrder[i].id) {
        const dragged = newOrder[i];
        const displaced = currentOrder[i];
        updateMutation.mutate({ id: dragged.id, updates: { sort_order: displaced.sort_order } });
        updateMutation.mutate({ id: displaced.id, updates: { sort_order: dragged.sort_order } });
        return;
      }
    }
  }

  const sectionIds = new Set(sections.map((s) => s.id));

  function moveItemToSection(item: ShoppingListItem, targetSectionId: string, sortOrder: number) {
    updateMutation.mutate({
      id: item.id,
      updates: { section_id: targetSectionId, sort_order: sortOrder },
    });
  }

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  function handlePageDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over) return;
    const activeId = active.id as string;
    const overId = over.id as string;
    const draggedItem = items.find((i) => i.id === activeId);
    if (!draggedItem) return;

    if (sectionIds.has(overId)) {
      if (overId !== draggedItem.section_id) {
        const targetItems = itemsForSection(overId);
        const maxOrder =
          targetItems.length === 0 ? 0 : Math.max(...targetItems.map((i) => i.sort_order)) + 1;
        moveItemToSection(draggedItem, overId, maxOrder);
      }
      return;
    }

    const overItem = items.find((i) => i.id === overId);
    if (!overItem) return;
    if (overItem.section_id === draggedItem.section_id) {
      const list = itemsForSection(draggedItem.section_id);
      const oldIndex = list.findIndex((i) => i.id === activeId);
      const newIndex = list.findIndex((i) => i.id === overId);
      if (oldIndex === -1 || newIndex === -1) return;
      const newOrder = arrayMove(list, oldIndex, newIndex);
      handleReorder(list, newOrder);
    } else {
      const targetItems = itemsForSection(overItem.section_id);
      const maxOrder =
        targetItems.length === 0 ? 0 : Math.max(...targetItems.map((i) => i.sort_order)) + 1;
      moveItemToSection(draggedItem, overItem.section_id, maxOrder);
    }
  }

  return (
    <div className="flex flex-col h-full bg-background min-h-0">
      {/* Fixed header – same as Manuals/Images/RouteGenerator */}
      <div className="relative z-20 flex h-[60px] flex-shrink-0 overflow-hidden border-b bg-background">
        <PageTitleBar
          className="h-full min-w-0 flex-1 border-0"
          rowClassName="h-full min-h-0 items-center gap-2"
          title={t("shoppingList.title")}
          right={
            <Button
              variant="outline"
              size="sm"
              className="hidden shrink-0 md:inline-flex"
              onClick={() => setNewSectionModalOpen(true)}
              aria-label={t("shoppingList.addNewList")}
            >
              <ListPlus className="h-4 w-4 sm:mr-1.5" />
              <span className="hidden sm:inline">{t("shoppingList.addNewList")}</span>
            </Button>
          }
        />
      </div>

      <div className="flex-1 min-h-0 overflow-auto p-4 pb-24">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handlePageDragEnd}
          >
            {sections.map((section) => {
              const sectionItems = itemsForSection(section.id);
              const inputs = getSectionInputs(section.id);
              return (
                <Column
                  key={section.id}
                  section={section}
                  title={section.name}
                  pendingCount={pendingCountForSection(section.id)}
                  items={sectionItems}
                  newTitle={inputs.title}
                  newNote={inputs.note}
                  onNewTitleChange={(v) =>
                    setSectionInputs((prev) => ({
                      ...prev,
                      [section.id]: { ...getSectionInputs(section.id), title: v },
                    }))
                  }
                  onNewNoteChange={(v) =>
                    setSectionInputs((prev) => ({
                      ...prev,
                      [section.id]: { ...getSectionInputs(section.id), note: v },
                    }))
                  }
                  onAdd={() => {
                    const title = inputs.title.trim();
                    if (!title) return;
                    createMutation.mutate({
                      section_id: section.id,
                      title,
                      note: inputs.note.trim() || undefined,
                    });
                  }}
                  createPending={createMutation.isPending}
                  onCheck={(item) => {
                    if (item.checked) {
                      updateMutation.mutate({
                        id: item.id,
                        updates: { checked: false },
                      });
                      return;
                    }
                    setCompletingId(item.id);
                setTimeout(() => {
                  updateMutation.mutate({
                    id: item.id,
                    updates: { checked: true },
                  });
                }, 180);
                  }}
                onDelete={handleDeleteItem}
                onDeleteSection={(section, itemCount) =>
                  setDeleteSectionState({ section, itemCount })
                }
                deletingId={deletingId}
                completingId={completingId}
                onReorder={(newOrder) => handleReorder(sectionItems, newOrder)}
                  updateMutation={updateMutation}
                  t={t}
                  formatItemDate={(iso, _t) => formatItemDate(iso, i18n.language)}
                />
              );
            })}
          </DndContext>
        </div>

        {listQuery.isLoading && (
          <>
            {/* ux-consistency: loading/empty C */}
            <Loading className="min-h-[32vh] py-8" />
          </>
        )}
      </div>

      {isMobile ? (
        <FabMenu
          fabAriaLabel={t("shoppingList.addNewList")}
          actions={[
            {
              id: "new-list",
              label: t("shoppingList.addNewList"),
              icon: <ListPlus className="h-5 w-5" />,
              onClick: () => setNewSectionModalOpen(true),
            },
          ]}
        />
      ) : null}

      {isMobile ? (
        <Sheet open={newSectionModalOpen} onOpenChange={setNewSectionModalOpen}>
          <SheetContent
            side="bottom"
            className={cn(
              bottomSheetDockClassName(),
              "flex max-h-[min(90dvh,720px)] flex-col overflow-hidden gap-0",
            )}
            onOpenAutoFocus={(e) => e.preventDefault()}
          >
            <div className="mx-auto mb-2 h-1 w-10 shrink-0 rounded-full bg-muted" aria-hidden />
            <SheetHeader className="max-[767px]:space-y-0 space-y-0 px-0 pb-2 pt-0 text-left">
              <SheetTitle className="text-base font-semibold leading-snug">
                {t("shoppingList.newSectionTitle")}
              </SheetTitle>
            </SheetHeader>
            <div className="min-h-0 flex-1 overflow-y-auto">
              <div className="grid gap-4 py-2">
                <Input
                  placeholder={t("shoppingList.newSectionPlaceholder")}
                  value={newSectionName}
                  onChange={(e) => setNewSectionName(e.target.value)}
                  onKeyDown={(e) =>
                    e.key === "Enter" &&
                    newSectionName.trim() &&
                    createSectionMutation.mutate({
                      name: newSectionName.trim(),
                      icon: newSectionIcon || null,
                    })
                  }
                />
                <div className="space-y-2">
                  <span className="text-sm font-medium">{t("shoppingList.sectionIcon")}</span>
                  <div className="flex flex-wrap gap-2">
                    {getSectionIconOptions(t).map((opt) =>
                      opt.value ? (
                        <Button
                          key={opt.value}
                          type="button"
                          variant={newSectionIcon === opt.value ? "secondary" : "outline"}
                          size="icon"
                          className="h-9 w-9"
                          onClick={() => setNewSectionIcon(opt.value)}
                          title={opt.label}
                          aria-label={opt.label}
                        >
                          <opt.Icon className="h-4 w-4" />
                        </Button>
                      ) : (
                        <Button
                          key="none"
                          type="button"
                          variant={newSectionIcon === "" ? "secondary" : "outline"}
                          size="sm"
                          className="h-9"
                          onClick={() => setNewSectionIcon("")}
                        >
                          —
                        </Button>
                      )
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="flex shrink-0 flex-col gap-2 border-t border-border pt-3 sm:flex-row sm:justify-end">
              <Button variant="outline" className="w-full sm:w-auto" onClick={() => setNewSectionModalOpen(false)}>
                {t("common.cancel")}
              </Button>
              <Button
                className="w-full sm:w-auto"
                onClick={() =>
                  newSectionName.trim() &&
                  createSectionMutation.mutate({
                    name: newSectionName.trim(),
                    icon: newSectionIcon || null,
                  })
                }
                disabled={!newSectionName.trim() || createSectionMutation.isPending}
              >
                {createSectionMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <ListPlus className="h-4 w-4 mr-2" />
                )}
                {t("shoppingList.addNewList")}
              </Button>
            </div>
          </SheetContent>
        </Sheet>
      ) : (
        <Dialog open={newSectionModalOpen} onOpenChange={setNewSectionModalOpen}>
          <DialogContent className="sm:max-w-md" onOpenAutoFocus={(e) => e.preventDefault()}>
            <DialogHeader>
              <DialogTitle>{t("shoppingList.newSectionTitle")}</DialogTitle>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <Input
                placeholder={t("shoppingList.newSectionPlaceholder")}
                value={newSectionName}
                onChange={(e) => setNewSectionName(e.target.value)}
                onKeyDown={(e) =>
                  e.key === "Enter" &&
                  newSectionName.trim() &&
                  createSectionMutation.mutate({
                    name: newSectionName.trim(),
                    icon: newSectionIcon || null,
                  })
                }
              />
              <div className="space-y-2">
                <span className="text-sm font-medium">{t("shoppingList.sectionIcon")}</span>
                <div className="flex flex-wrap gap-2">
                  {getSectionIconOptions(t).map((opt) =>
                    opt.value ? (
                      <Button
                        key={opt.value}
                        type="button"
                        variant={newSectionIcon === opt.value ? "secondary" : "outline"}
                        size="icon"
                        className="h-9 w-9"
                        onClick={() => setNewSectionIcon(opt.value)}
                        title={opt.label}
                        aria-label={opt.label}
                      >
                        <opt.Icon className="h-4 w-4" />
                      </Button>
                    ) : (
                      <Button
                        key="none"
                        type="button"
                        variant={newSectionIcon === "" ? "secondary" : "outline"}
                        size="sm"
                        className="h-9"
                        onClick={() => setNewSectionIcon("")}
                      >
                        —
                      </Button>
                    )
                  )}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setNewSectionModalOpen(false)}>
                {t("common.cancel")}
              </Button>
              <Button
                onClick={() =>
                  newSectionName.trim() &&
                  createSectionMutation.mutate({
                    name: newSectionName.trim(),
                    icon: newSectionIcon || null,
                  })
                }
                disabled={!newSectionName.trim() || createSectionMutation.isPending}
              >
                {createSectionMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <ListPlus className="h-4 w-4 mr-2" />
                )}
                {t("shoppingList.addNewList")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      <Dialog
        open={!!deleteSectionState}
        onOpenChange={(open) => !open && setDeleteSectionState(null)}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {t("shoppingList.deleteSectionTitle")}{" "}
              {deleteSectionState?.section.name}
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            {deleteSectionState && deleteSectionState.itemCount > 0 ? (
              <>
                <p className="text-sm text-muted-foreground">
                  {t("shoppingList.deleteSectionWithItems", {
                    count: deleteSectionState.itemCount,
                  })}
                </p>
                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    {t("shoppingList.moveItemsTo")}
                  </label>
                  <select
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={deleteSectionMoveToId}
                    onChange={(e) => setDeleteSectionMoveToId(e.target.value)}
                  >
                    <option value="">—</option>
                    {sections
                      .filter((s) => s.id !== deleteSectionState.section.id)
                      .map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                  </select>
                </div>
                <DialogFooter className="flex-col sm:flex-row gap-2">
                  <Button
                    variant="default"
                    className="sm:mr-auto"
                    disabled={
                      !deleteSectionMoveToId || deleteSectionMutation.isPending
                    }
                    onClick={() =>
                      deleteSectionState &&
                      deleteSectionMoveToId &&
                      deleteSectionMutation.mutate({
                        sectionId: deleteSectionState.section.id,
                        moveTo: deleteSectionMoveToId,
                      })
                    }
                  >
                    {deleteSectionMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : null}
                    {t("shoppingList.deleteSectionMove")}
                  </Button>
                  <Button
                    variant="destructive"
                    disabled={deleteSectionMutation.isPending}
                    onClick={() =>
                      deleteSectionState &&
                      deleteSectionMutation.mutate({
                        sectionId: deleteSectionState.section.id,
                        deleteItems: true,
                      })
                    }
                  >
                    {t("shoppingList.deleteSectionAndItems")}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setDeleteSectionState(null)}
                  >
                    {t("common.cancel")}
                  </Button>
                </DialogFooter>
              </>
            ) : deleteSectionState ? (
              <>
                <p className="text-sm text-muted-foreground">
                  {t("shoppingList.deleteSectionEmpty")}
                </p>
                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => setDeleteSectionState(null)}
                  >
                    {t("common.cancel")}
                  </Button>
                  <Button
                    variant="destructive"
                    disabled={deleteSectionMutation.isPending}
                    onClick={() =>
                      deleteSectionMutation.mutate({
                        sectionId: deleteSectionState.section.id,
                      })
                    }
                  >
                    {deleteSectionMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Trash2 className="h-4 w-4 mr-2" />
                    )}
                    {t("common.delete")}
                  </Button>
                </DialogFooter>
              </>
            ) : null}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Column({
  section,
  title,
  pendingCount,
  items,
  newTitle,
  newNote,
  onNewTitleChange,
  onNewNoteChange,
  onAdd,
  createPending,
  onCheck,
  onDelete,
  onDeleteSection,
  deletingId,
  completingId,
  onReorder,
  updateMutation,
  t,
  formatItemDate,
}: {
  section: ShoppingListSection;
  title: string;
  pendingCount: number;
  items: ShoppingListItem[];
  newTitle: string;
  newNote: string;
  onNewTitleChange: (v: string) => void;
  onNewNoteChange: (v: string) => void;
  onAdd: () => void;
  createPending: boolean;
  onCheck: (item: ShoppingListItem) => void;
  onDelete: (id: string) => void;
  onDeleteSection: (section: ShoppingListSection, itemCount: number) => void;
  deletingId: string | null;
  completingId: string | null;
  onReorder?: (newOrder: ShoppingListItem[]) => void;
  updateMutation: { isPending: boolean };
  t: (key: string) => string;
  formatItemDate: (iso: string | null | undefined, t: (k: string) => string) => string;
}) {
  const [completedCollapsed, setCompletedCollapsed] = useState(true);
  const active = items.filter((i) => !i.checked).sort((a, b) => a.sort_order - b.sort_order);
  const completed = items
    .filter((i) => i.checked)
    .sort((a, b) => {
      const aAt = a.completed_at || a.updated_at;
      const bAt = b.completed_at || b.updated_at;
      return (bAt || "").localeCompare(aAt || "");
    });

  const { setNodeRef, isOver } = useDroppable({ id: section.id });

  return (
    <div className="flex flex-col rounded-lg border border-border bg-card overflow-hidden">
      <div className="flex items-center gap-2 p-3 border-b border-border bg-muted/30">
        <SectionIcon section={section} />
        <span className="font-semibold flex-1 min-w-0 truncate">{title}</span>
        {pendingCount > 0 && (
          <span className="rounded-full bg-primary/20 text-primary px-2 py-0.5 text-xs font-medium shrink-0">
            {pendingCount}
          </span>
        )}
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
          onClick={() => onDeleteSection(section, items.length)}
          aria-label={t("shoppingList.deleteList")}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
      <div
        ref={setNodeRef}
        className={cn(
          "p-3 flex-1 min-h-0 overflow-auto flex flex-col",
          isOver && "bg-primary/5 ring-1 ring-primary/20 rounded-b-lg"
        )}
      >
        {/* Add a task - one line at top */}
        <div className="flex items-center gap-2 mb-3">
          <Input
            placeholder={t("shoppingList.addTaskPlaceholder")}
            value={newTitle}
            onChange={(e) => onNewTitleChange(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && onAdd()}
            className="flex-1 rounded-full bg-muted/50 border-0 focus-visible:ring-2"
          />
          <Button
            size="icon"
            variant="ghost"
            onClick={onAdd}
            disabled={!newTitle.trim() || createPending}
            className="shrink-0 rounded-full h-9 w-9"
            aria-label={t("shoppingList.add")}
          >
            {createPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          </Button>
        </div>

        {/* Active items - no section title, just list */}
        <section className="flex-1 min-h-0">
          {active.length === 0 ? (
            <>
              {/* ux-consistency: loading/empty C */}
              <AppEmptyState icon={ShoppingCart} title={t("shoppingList.empty")} className="py-4" />
            </>
          ) : (
            <SortableContext
              items={active.map((i) => i.id)}
              strategy={verticalListSortingStrategy}
            >
              <ul className="overflow-x-hidden">
                {active.map((item) => (
                  <SortableItemRow
                    key={item.id}
                    item={item}
                    isDeleting={deletingId === item.id}
                    isCompleting={completingId === item.id}
                    onCheck={onCheck}
                    onDelete={onDelete}
                    t={t}
                    formatItemDate={formatItemDate}
                  />
                ))}
              </ul>
            </SortableContext>
          )}
        </section>

        {/* Collapsible Completed */}
        <section className="mt-auto pt-2 border-t border-border">
          <button
            type="button"
            onClick={() => setCompletedCollapsed(!completedCollapsed)}
            className="flex items-center gap-2 w-full py-2 text-sm font-medium text-muted-foreground hover:text-foreground"
          >
            {completedCollapsed ? (
              <ChevronRight className="h-4 w-4 shrink-0" />
            ) : (
              <ChevronDown className="h-4 w-4 shrink-0" />
            )}
            {t("shoppingList.completedSection")} ({completed.length})
          </button>
          {!completedCollapsed && (
            completed.length === 0 ? (
              <p className="text-sm text-muted-foreground py-2 pl-6">—</p>
            ) : (
              <ul className="overflow-x-hidden">
                {completed.map((item) => (
                  <ItemRow
                    key={item.id}
                    item={item}
                    isDeleting={deletingId === item.id}
                    onCheck={onCheck}
                    onDelete={onDelete}
                    t={t}
                    formatItemDate={formatItemDate}
                    compact
                  />
                ))}
              </ul>
            )
          )}
        </section>
      </div>
    </div>
  );
}

function SortableItemRow({
  item,
  isDeleting,
  isCompleting,
  onCheck,
  onDelete,
  t,
  formatItemDate,
}: {
  item: ShoppingListItem;
  isDeleting: boolean;
  isCompleting: boolean;
  onCheck: (item: ShoppingListItem) => void;
  onDelete: (id: string) => void;
  t: (key: string) => string;
  formatItemDate: (iso: string | null | undefined, t: (k: string) => string) => string;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const exitingDeleteStyle = {
    transform: "translateX(100%)",
    opacity: 0,
    maxHeight: 0,
    paddingTop: 0,
    paddingBottom: 0,
    margin: 0,
    overflow: "hidden" as const,
    transition:
      "transform 0.28s ease-out, opacity 0.28s ease-out, max-height 0.28s ease-out, padding 0.2s ease-out, margin 0.2s ease-out",
  };

  const exitingCompleteStyle = {
    maxHeight: 0,
    paddingTop: 0,
    paddingBottom: 0,
    margin: 0,
    overflow: "hidden" as const,
    transition:
      "max-height 0.28s ease-out 0.12s, padding 0.2s ease-out 0.12s, margin 0.2s ease-out 0.12s",
  };

  const style =
    isDeleting
      ? exitingDeleteStyle
      : isCompleting
        ? exitingCompleteStyle
        : {
            transform: CSS.Transform.toString(transform),
            transition,
          };

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={cn(
        "shopping-list-item flex items-center gap-2 py-2 px-1 rounded-sm hover:bg-muted/40 group",
        isDragging && "opacity-60 z-10",
        isDeleting && "shopping-list-item-exiting-delete",
        isCompleting && "shopping-list-item-exiting-complete"
      )}
    >
      <button
        type="button"
        className="shrink-0 p-1 rounded touch-none cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground"
        aria-label={t("shoppingList.dragHandle")}
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <div className="flex-1 min-w-0">
        <span
          className={cn(
            "font-medium inline-block",
            isCompleting && "shopping-list-item-strikethrough"
          )}
        >
          {item.title}
        </span>
        {item.note && (
          <p className="text-xs text-muted-foreground mt-0.5 truncate">{item.note}</p>
        )}
      </div>
      <div className="flex items-center gap-0.5 shrink-0 opacity-70 group-hover:opacity-100 transition-opacity">
        <Checkbox
          checked={item.checked}
          onCheckedChange={() => onCheck(item)}
          aria-label={item.title}
          className="shrink-0"
        />
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-destructive hover:text-destructive"
          onClick={() => onDelete(item.id)}
          aria-label={t("common.delete")}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </li>
  );
}

function ItemRow({
  item,
  isDeleting,
  onCheck,
  onDelete,
  t,
  formatItemDate,
  compact = false,
}: {
  item: ShoppingListItem;
  isDeleting: boolean;
  onCheck: (item: ShoppingListItem) => void;
  onDelete: (id: string) => void;
  t: (key: string) => string;
  formatItemDate: (iso: string | null | undefined, t: (k: string) => string) => string;
  compact?: boolean;
}) {
  return (
    <li
      className={cn(
        "shopping-list-item flex items-center gap-2 py-2 px-1 rounded-sm hover:bg-muted/40 group",
        item.checked && "opacity-80",
        isDeleting && "shopping-list-item-exiting-delete"
      )}
    >
      <div className="flex-1 min-w-0">
        <span className={cn("font-medium", item.checked && "line-through text-muted-foreground")}>
          {item.title}
        </span>
        {item.note && (
          <p className="text-xs text-muted-foreground mt-0.5 truncate">{item.note}</p>
        )}
        {!compact && (
          <p className="text-[10px] text-muted-foreground mt-1">
            {t("shoppingList.addedAt")}: {formatItemDate(item.created_at, t)}
            {item.completed_at && (
              <>
                {" · "}
                {t("shoppingList.completedAt")}: {formatItemDate(item.completed_at, t)}
              </>
            )}
          </p>
        )}
      </div>
      <div className="flex items-center gap-0.5 shrink-0 opacity-70 group-hover:opacity-100 transition-opacity">
        <Checkbox
          checked={item.checked}
          onCheckedChange={() => onCheck(item)}
          aria-label={item.title}
          className="shrink-0"
        />
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-destructive hover:text-destructive"
          onClick={() => onDelete(item.id)}
          aria-label={t("common.delete")}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </li>
  );
}

export default ShoppingListPage;
